/**
 * **La reprise d'une production en cours, proposée au boot** — SPEC-UI §1.7 (« `production !=
 * null` → proposer la reprise de l'Atelier (dialogue maison) ») et §3.3 (« on reprend à l'étape
 * courante ; une seule production en cours à la fois »).
 *
 * Composant sans rendu, monté à la racine : il attend que la run soit restituée, lit la
 * sauvegarde du brouillon (`atelierStore`), et — si elle vaut pour cette run à ce tour —
 * demande au joueur, **une fois par run et par chargement**, s'il reprend. Oui : le store est
 * restauré et on entre dans l'Atelier ; non : la sauvegarde est jetée, la production est perdue
 * (rien n'avait été dépensé — la production ne coûte qu'à la publication, one-shot A14).
 *
 * Si le tour a avancé depuis (un autre onglet a joué), on ne propose rien : reprendre ferait
 * noter des gestes sur une autre partition. On le dit, en un toast, et on efface.
 */

import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { contenu } from "../../game/content";
import { ETAPES } from "../../game/format";
import { interrupteurs } from "../../game/menage";
import {
  effacerSauvegarde,
  etapesDuFormat,
  lireSauvegarde,
  useAtelierStore,
  verdictReprise,
} from "../../stores/atelierStore";
import { useRunStore } from "../../stores/runStore";
import { dialog, useUIStore } from "../../stores/uiStore";

export function RepriseAtelier() {
  const run = useRunStore((s) => s.run);
  const pret = useRunStore((s) => s.pret);
  const brouillon = useAtelierStore((s) => s.brouillon);
  const restaurer = useAtelierStore((s) => s.restaurer);
  const toast = useUIStore((s) => s.toast);
  const naviguer = useNavigate();
  const emplacement = useLocation();
  /** La run pour laquelle la question a déjà été posée — on ne la repose pas à chaque rendu. */
  const demandePour = useRef<string | null>(null);

  useEffect(() => {
    if (!pret || !run || brouillon !== null) return;
    if (emplacement.pathname === "/atelier") return;
    if (demandePour.current === run.runId) return;
    demandePour.current = run.runId;

    const sauvegarde = lireSauvegarde();
    // ⟦C60 · A-177⟧ Une production en mode jalon sauvegardée AVANT le ménage ne se reprend plus : les jalons
    // n'existent plus (le moteur refuserait `video_jalon`, en `mecanique_verrouillee`). On la jette sans
    // bruit, le joueur n'a jamais vu de jalon dans cette partie-là. Rallumés, ils reviennent.
    if (sauvegarde?.brouillon.jalonId && !interrupteurs().jalons) {
      effacerSauvegarde();
      return;
    }
    const verdict = verdictReprise(sauvegarde, run);
    if (verdict === "aucune" || verdict === "autre_run") return;
    if (verdict === "tour_avance") {
      effacerSauvegarde();
      toast({
        // ⟦C57 · lot 8⟧ Une phrase courte ; la cause (« un autre onglet ? ») est dans `BlocServeur`.
        texte: "Production perdue : la partie a avancé.",
        ton: "mal",
        icone: "🎬",
      });
      return;
    }

    const s = sauvegarde!;
    const c = contenu();
    const titre =
      (s.brouillon.jalonId && c.milestones.find((j) => j.id === s.brouillon.jalonId)?.nom) ||
      c.parSujet.get(s.brouillon.subjectId)?.nom ||
      s.brouillon.subjectId;
    const jouees = etapesDuFormat(s.brouillon.format).filter(
      (stage) => s.brouillon.embauches?.[stage] === undefined,
    );
    const courante = jouees[Math.min(s.index, jouees.length - 1)];
    // ⟦C57 · lot 8 · A-174⟧ Le titre de la vidéo, puis OÙ on en était : « Écriture, 1/3 » ou « Prête à
    // publier ». Les phrases d'avant (les étapes notées gardent leur score, une étape commencée reprend à
    // son intro, rien n'a été dépensé) sont derrière « Les dessous » de l'Atelier (`BlocProduction`).
    const avancement =
      s.index >= jouees.length
        ? "Prête à publier."
        : `${courante ? ETAPES[courante].nom : "Une étape"}, ${s.index + 1}/${jouees.length}.`;

    void (async () => {
      const ok = await dialog.confirm({
        title: "Reprendre ta production ?",
        body: `« ${titre} ». ${avancement}`,
        confirmLabel: "Reprendre",
        cancelLabel: "Jeter le brouillon",
      });
      if (!ok) {
        effacerSauvegarde();
        return;
      }
      restaurer(s);
      naviguer("/atelier");
    })();
  }, [pret, run, brouillon, emplacement.pathname, restaurer, naviguer, toast]);

  return null;
}
